import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CartItem, Customization, Product, ProductId } from "../types";
import {
  addToCart,
  cartCount,
  cartSubtotal,
  CART_STORAGE_KEY,
  decrementInCart,
  incrementInCart,
  quantityOfProduct,
  readStoredCart,
  removeFromCart,
} from "./cartActions";

interface CartContextValue {
  items: CartItem[];
  /** Soma das quantidades. */
  count: number;
  subtotal: number;
  /** Incrementa a cada item adicionado — dispara a animação do ícone do carrinho. */
  bump: number;
  /** Unidades do produto no carrinho, somando todas as personalizações. */
  quantityOf: (id: ProductId) => number;
  addItem: (product: Product, customization?: Customization | null, quantity?: number) => void;
  /** As operações abaixo usam o lineId (lanche sem personalização: String(product.id)). */
  incrementItem: (lineId: string) => void;
  decrementItem: (lineId: string) => void;
  removeItem: (lineId: string) => void;
  clearCart: () => void;

  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  isCheckoutOpen: boolean;
  openCheckout: () => void;
  closeCheckout: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(readStoredCart);
  const [bump, setBump] = useState(0);
  const [isCartOpen, setCartOpen] = useState(false);
  const [isCheckoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Sem localStorage o carrinho continua funcionando em memória.
    }
  }, [items]);

  const addItem = useCallback((product: Product, customization: Customization | null = null, quantity = 1) => {
    setItems((prev) => addToCart(prev, product, customization, quantity));
    setBump((b) => b + 1);
  }, []);

  const incrementItem = useCallback((lineId: string) => {
    setItems((prev) => incrementInCart(prev, lineId));
    setBump((b) => b + 1);
  }, []);

  const decrementItem = useCallback((lineId: string) => {
    setItems((prev) => decrementInCart(prev, lineId));
  }, []);

  const removeItem = useCallback((lineId: string) => {
    setItems((prev) => removeFromCart(prev, lineId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const quantityOf = useCallback((id: ProductId) => quantityOfProduct(items, id), [items]);

  const openCart = useCallback(() => setCartOpen(true), []);
  const closeCart = useCallback(() => setCartOpen(false), []);
  const openCheckout = useCallback(() => {
    setCartOpen(false);
    setCheckoutOpen(true);
  }, []);
  const closeCheckout = useCallback(() => setCheckoutOpen(false), []);

  const value = useMemo<CartContextValue>(
    () => ({
      items,
      count: cartCount(items),
      subtotal: cartSubtotal(items),
      bump,
      quantityOf,
      addItem,
      incrementItem,
      decrementItem,
      removeItem,
      clearCart,
      isCartOpen,
      openCart,
      closeCart,
      isCheckoutOpen,
      openCheckout,
      closeCheckout,
    }),
    [items, bump, quantityOf, addItem, incrementItem, decrementItem, removeItem, clearCart, isCartOpen, openCart, closeCart, isCheckoutOpen, openCheckout, closeCheckout],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart precisa estar dentro de <CartProvider>");
  return ctx;
};
